import type { CertificateIssueInput, CertificateTemplate } from './types';
import { formatTimestampDate } from '../../../shared/formatTimestampDate';

export const certificatePurposeOptions: ReadonlyArray<{
  value: CertificateIssueInput['purposeCode'];
  label: string;
}> = [
  { value: 'FINANCIAL_INSTITUTION', label: '금융기관 제출' },
  { value: 'PUBLIC_AGENCY', label: '공공기관 제출' },
  { value: 'VISA', label: '비자·출입국' },
  { value: 'SCHOOL', label: '학교 제출' },
  { value: 'OTHER', label: '기타' },
];

export function displayCertificateDate(value?: string) {
  return formatTimestampDate(value);
}

export function certificateStatusLabel(value: string) {
  return ({
    ISSUED: '발급 완료',
    REQUESTED: '처리 중',
    FAILED: '확인 필요',
    CANCELLED: '취소',
    VALID: '유효',
    REVOKED: '폐기',
  } as Record<string, string>)[value] ?? value;
}

export function certificateStatusTone(value: string) {
  if (value === 'ISSUED' || value === 'VALID' || value === 'CLEAN') return 'success' as const;
  if (value === 'FAILED' || value === 'REQUESTED' || value === 'PENDING') return 'warning' as const;
  return 'neutral' as const;
}

function certificateErrorCode(reason: unknown) {
  if (!(reason instanceof Error) || !('code' in reason)) return undefined;
  return typeof reason.code === 'string' ? reason.code : undefined;
}

export function certificateIssueError(reason: unknown) {
  const code = certificateErrorCode(reason);
  if (code === 'SOURCE_DATA_INCOMPLETE') return '발급에 필요한 인사 정보가 부족합니다. HR 담당자에게 원장 확인을 요청해 주세요.';
  if (code === 'NOT_ELIGIBLE') return '현재 재직 상태 또는 발급 정책에서는 이 증명서를 자동 발급할 수 없습니다.';
  return reason instanceof Error ? reason.message : '증명서를 발급하지 못했습니다.';
}

export function certificateSettingSaveError(reason: unknown) {
  if (certificateErrorCode(reason) === 'VERSION_CONFLICT') {
    return '다른 관리자가 먼저 증명서 설정을 변경했습니다. 최신 설정을 불러왔으니 변경 내용을 다시 확인해 주세요.';
  }
  return reason instanceof Error ? reason.message : '증명서 설정을 저장하지 못했습니다.';
}

export function isCertificateVersionConflict(reason: unknown) {
  return certificateErrorCode(reason) === 'VERSION_CONFLICT';
}

export function certificatePurposeLabel(value: CertificateIssueInput['purposeCode']) {
  return certificatePurposeOptions.find((option) => option.value === value)?.label ?? value;
}

export function certificatePurpose(value: CertificateIssueInput['purposeCode'], destination: string) {
  const normalizedDestination = destination.trim();
  if (normalizedDestination) return `${normalizedDestination} 제출용`;
  const label = certificatePurposeLabel(value);
  return label.endsWith('제출') ? `${label}용` : `${label} 제출용`;
}

export function certificateSheetDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[1]}년 ${match[2]}월 ${match[3]}일` : value;
}

export function certificateTemplatePolicyKey(template: CertificateTemplate) {
  return JSON.stringify({
    displayName: template.displayName,
    statement: template.statement,
    requireSeal: template.requireSeal,
    autoIssue: template.autoIssue,
    validityDays: template.validityDays,
    verificationPublic: template.verificationPublic,
    enabled: template.enabled,
  });
}

export function certificateTemplatePolicyErrors(template: CertificateTemplate) {
  return {
    displayName: template.displayName.trim() ? undefined : '표시 이름을 입력해 주세요.',
    statement: template.statement.trim() ? undefined : '확인 문구를 입력해 주세요.',
    validityDays: Number.isInteger(template.validityDays) && template.validityDays >= 1 && template.validityDays <= 3650
      ? undefined
      : '유효기간은 1일부터 3650일까지 정수로 입력해 주세요.',
  };
}
